import { z } from 'zod';

/**
 * The platform's own answer to "what time is it?", kept once.
 *
 * Every OLT already carries the servers it should ask (`olts.ntp_servers`).
 * What was missing is the set a NEW chassis starts from, so each one was typed
 * in by hand and a fleet grown one OLT at a time ends up with four different
 * answers to a question that has one — and the one chassis somebody forgot is
 * the one whose event log cannot be read against the others a year later.
 *
 * `scope` is the literal `platform` or an organization id, the same shape
 * branding uses. An org's own row wins; the platform row is what an org
 * without one inherits.
 */
export const NTP_PLATFORM_SCOPE = 'platform';

/** A chassis has no use for more time servers than this, and ZTE ranks 1-8. */
export const NTP_MAX_SERVERS = 8;

/**
 * Where an effective set came from, so the UI never implies an org configured
 * something it merely inherited.
 */
export const ntpSettingsSourceSchema = z.enum(['org', 'platform', 'none']);
export type NtpSettingsSource = z.infer<typeof ntpSettingsSourceSchema>;

export const ntpSettingsSchema = z.object({
  scope: z.string(),
  servers: z.array(z.string()),
  /** IANA zone name, e.g. `Africa/Nairobi`. The default a new OLT starts with. */
  timezone: z.string().nullable(),
  /** ZTE `ntp poll-interval`: a power-of-two exponent, 4-17. */
  ztePollInterval: z.number().int().nullable(),
  /** Huawei `ntp-service sync-interval`: seconds, 64-1024. */
  huaweiSyncInterval: z.number().int().nullable(),
  updatedAt: z.number().int().nullable(),
  updatedBy: z.string().nullable(),
  source: ntpSettingsSourceSchema,
});
export type NtpSettings = z.infer<typeof ntpSettingsSchema>;

export const updateNtpSettingsSchema = z.object({
  servers: z.array(z.string().min(1).max(255)).max(NTP_MAX_SERVERS),
  timezone: z.string().max(64).nullable().optional(),
  ztePollInterval: z.coerce.number().int().min(4).max(17).nullable().optional(),
  huaweiSyncInterval: z.coerce.number().int().min(64).max(1024).nullable().optional(),
  /** Superadmin editing one tenant's set instead of the platform default. */
  scope: z.string().max(64).optional(),
});
export type UpdateNtpSettingsRequest = z.infer<typeof updateNtpSettingsSchema>;

/**
 * Public pools worth offering, with what each one actually is.
 *
 * Suggestions only — nothing is applied until an operator saves it. They are
 * listed rather than generated because "which NTP server" is a question with a
 * small number of right answers and a large number of plausible typos.
 */
export const SUGGESTED_NTP_SERVERS: Array<{ host: string; label: string }> = [
  { host: 'time.google.com', label: 'Google — anycast, smeared leap seconds' },
  { host: 'time.cloudflare.com', label: 'Cloudflare — anycast' },
  { host: 'pool.ntp.org', label: 'NTP Pool — global' },
  { host: 'africa.pool.ntp.org', label: 'NTP Pool — Africa' },
  { host: 'europe.pool.ntp.org', label: 'NTP Pool — Europe' },
  { host: 'asia.pool.ntp.org', label: 'NTP Pool — Asia' },
  { host: 'north-america.pool.ntp.org', label: 'NTP Pool — North America' },
];

/**
 * Whether the runtime knows this zone name.
 *
 * Asked of `Intl` rather than of a list kept here: the tzdata a process holds
 * is the only authority on which names it can convert, and a name this
 * platform accepts but cannot convert would render a timezone command with a
 * guessed offset in it.
 */
export function isKnownTimeZone(zone: string): boolean {
  if (!zone || zone.length > 64) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Every zone the runtime knows, for the selector.
 *
 * `Intl.supportedValuesOf` is Node 18+ and every browser this app supports;
 * the short fallback exists so an older runtime gets a usable list rather than
 * an empty select.
 */
export function knownTimeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  if (typeof intl.supportedValuesOf === 'function') {
    try {
      const zones = intl.supportedValuesOf('timeZone');
      if (Array.isArray(zones) && zones.length > 0) return zones;
    } catch {
      /* fall through to the short list */
    }
  }
  return [
    'UTC',
    'Africa/Nairobi',
    'Africa/Lagos',
    'Africa/Johannesburg',
    'Europe/London',
    'Europe/Berlin',
    'Asia/Dubai',
    'Asia/Kolkata',
    'America/New_York',
    'America/Los_Angeles',
  ];
}

/**
 * How far a named zone sits from UTC at a given moment, in minutes.
 *
 * Returns null for a name the runtime does not know, so nothing downstream
 * renders a chassis command around a guessed offset. Positive is east of UTC:
 * `Africa/Nairobi` is +180.
 */
export function zoneOffsetMinutes(zone: string, at: number = Date.now()): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(new Date(at));
    const get = (type: string): number =>
      Number(parts.find((p) => p.type === type)?.value ?? Number.NaN);
    const local = Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour') % 24,
      get('minute'),
      get('second'),
    );
    if (!Number.isFinite(local)) return null;
    return Math.round((local - at) / 60_000);
  } catch {
    return null;
  }
}

/**
 * `UTC+03:00` for a zone name, or the bare name when the runtime cannot place
 * it — a label, for a picker where two zones with the same offset are the same
 * clock and two that look alike are not.
 */
export function describeZoneLabel(zone: string, at: number = Date.now()): string {
  const offset = zoneOffsetMinutes(zone, at);
  return offset === null ? zone : `UTC${formatZoneOffset(offset)}`;
}

/** `+03:00` / `-05:30` / `+00:00`, from a minute offset. */
export function formatZoneOffset(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
}

// --- scheduling in somebody else's clock --------------------------------------

/**
 * Resolving "at 02:00" against the right clock.
 *
 * A schedule that names an HOUR is a statement about somebody's local time,
 * and on this platform that somebody is usually the chassis, not the server.
 * The server can sit in Nairobi (UTC+3) while a chassis sits in Accra (UTC+0):
 * "back up at 02:00", read on the server's clock, fires at 23:00 the previous
 * evening in Accra — the wrong day's configuration, filed under today's date,
 * and an operator comparing the backup timestamp against the chassis's own
 * event log cannot reconcile the two.
 *
 * So per-OLT work asks these helpers, with the OLT's zone, and everything
 * platform-wide passes null and gets the server's own clock. A zone the
 * runtime cannot place falls back to the server too rather than throwing: a
 * schedule that stops running because a zone name was mistyped is a worse
 * failure than one that runs an hour out and says which clock it used.
 *
 * `Intl` is the only tzdata here, the same as everywhere else in this file —
 * no dependency, and it already knows about every zone the picker offers.
 */

/** The zone the server process itself is in, e.g. `Africa/Nairobi`. */
export function serverTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * The hour it is in `zone` at this instant, 0-23.
 *
 * Null/empty/unknown zone means the server's own clock, which is the
 * behaviour every schedule had before any of them knew about zones.
 */
export function hourInZone(zone: string | null | undefined, at: number = Date.now()): number {
  const offset = zone ? zoneOffsetMinutes(zone, at) : null;
  if (offset === null) return new Date(at).getHours();
  return new Date(at + offset * 60_000).getUTCHours();
}

/**
 * Midnight at the start of the local day containing `at`, as a UTC instant.
 *
 * This is what "once a day" has to be measured against: a per-OLT daily
 * backup is one per the CHASSIS's day, so an OLT whose local day has already
 * turned is due again even though the server is still on yesterday.
 *
 * The offset is re-read at the candidate midnight because a zone that changed
 * offset between midnight and now (a DST boundary) would otherwise land an
 * hour either side of it. None of the four zones this fleet runs in observe
 * DST; that is not a reason to get it wrong for the fifth.
 */
export function startOfDayInZone(
  zone: string | null | undefined,
  at: number = Date.now(),
): number {
  const offset = zone ? zoneOffsetMinutes(zone, at) : null;
  if (offset === null) {
    const local = new Date(at);
    local.setHours(0, 0, 0, 0);
    return local.getTime();
  }
  const shifted = new Date(at + offset * 60_000);
  const midnightLocal = Date.UTC(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth(),
    shifted.getUTCDate(),
  );
  const candidate = midnightLocal - offset * 60_000;
  const atCandidate = zone ? zoneOffsetMinutes(zone, candidate) : null;
  if (atCandidate !== null && atCandidate !== offset) return midnightLocal - atCandidate * 60_000;
  return candidate;
}

/**
 * The UTC instant at which `hour` strikes on the local day containing `at`.
 *
 * The whole point, in one function: `instantOfLocalHour('Africa/Accra', t, 2)`
 * and `instantOfLocalHour('Africa/Nairobi', t, 2)` are three hours apart, and
 * a schedule that compares `Date.now()` against the right one of those fires
 * when the operator said it would on the chassis they said it about.
 *
 * Hours beyond 0-23 are not clamped — `hour` comes from settings that already
 * bound it, and silently moving a schedule is worse than arithmetic that says
 * what it was given.
 */
export function instantOfLocalHour(
  zone: string | null | undefined,
  at: number,
  hour: number,
): number {
  return startOfDayInZone(zone, at) + hour * 3_600_000;
}

/**
 * How much a rendered timezone command is actually known to be right.
 *
 * Three levels, and only one of them is a yes:
 *
 *   captured        — the exact command, with its arguments, was read back off
 *                     a chassis of this vendor. Safe to send.
 *   running-config  — the setting appears in a running config, which is a set
 *                     of commands the chassis accepted, but the printed form
 *                     may be a display rather than the input. Likely, not sure.
 *   keyword-only    — the chassis's help lists the keyword and nothing about
 *                     its arguments. A guess with a good pedigree is still a
 *                     guess.
 *
 * ZTE is `captured` — `clock timezone ?` was typed at a live C300 and its
 * answers pasted back. Huawei is not, and the gate it sits behind lifted for
 * ZTE alone because this is data rather than a hardcoded flag.
 */
export const timezoneEvidenceSchema = z.enum(['captured', 'running-config', 'keyword-only']);
export type TimezoneEvidence = z.infer<typeof timezoneEvidenceSchema>;

export const timezonePlanSchema = z.object({
  zone: z.string(),
  offsetMinutes: z.number().int(),
  commands: z.array(z.string()),
  evidence: timezoneEvidenceSchema,
  /** true only for `captured`. The one flag a send may be gated on. */
  verified: z.boolean(),
  /** What is known and what is assumed, in the operator's own words. */
  note: z.string(),
  /** The exact thing to type on the chassis to settle it. */
  confirmOnHardware: z.string(),
});
export type TimezonePlan = z.infer<typeof timezonePlanSchema>;

/**
 * POST /api/olts/:id/time/management-ntp — an OLT keeps time from the
 * platform's own host, over its management path (operator, 2026-10-06:
 * "build the route and vrf ntp into the platform"). Dry-run by default.
 */
export const managementNtpRequestSchema = z.object({
  dryRun: z.boolean().default(true),
  /** The site router on the management subnet; read from the chassis's ARP table when omitted. */
  nextHop: z.string().ip({ version: 'v4' }).nullable().optional(),
  /** The platform's host as the chassis reaches it; CWMP_URL's host when omitted. */
  server: z.string().ip({ version: 'v4' }).nullable().optional(),
});
export type ManagementNtpRequest = z.infer<typeof managementNtpRequestSchema>;

/** What the chassis would get (dry run) or got, and what it said after. */
export interface ManagementNtpResult {
  dryRun: boolean;
  server: string;
  /** The VRF the management port lives in (a C650's `mgmt_eth`: `mng`); null for the global table. */
  vrf: string | null;
  interface: string | null;
  /** The chassis's ping to the platform's host before anything was written. */
  reachable: boolean | null;
  needsRoute: boolean;
  nextHop: string | null;
  nextHopSource: 'given' | 'arp' | null;
  /** Every neighbour on the management subnet, for choosing the site router. */
  nextHopCandidates: string[];
  needsServer: boolean;
  priority: number | null;
  commands: string[];
  blockers: string[];
  warnings: string[];
  unverified: string[];
  executed: boolean;
  refused: string[];
  reachableAfter: boolean | null;
  synchronised: boolean | null;
  following: string | null;
  /** One sentence for the operator. */
  note: string;
}
